import {Component, Input, OnDestroy} from '@angular/core';
import {IReferral, IUser} from "../../../../@core/interfaces/user/user.interface";
import {UserService} from "../../../../@shared/services/user.service";
import {ReferralInfoComponent} from "../../modals/referral-info/referral-info.component";
import {MatDialog} from "@angular/material/dialog";
import {ICommonStat, ITopPanel} from "../services/interfaces/common-stats.interface";
import {AddBalanceComponent} from "../../modals/add-balance/add-balance.component";
import {ActivatedRoute, Router} from "@angular/router";
import {StatsService} from "../../../+stats/services/stats.service";
import {combineLatest, Observable, Subject} from "rxjs";
import {map, takeUntil} from "rxjs/operators";
import * as moment from "moment";
import {WalletTypesService} from "../../../../@core/services/wallet-types.service";
import {WalletTypeIdEnum} from "../../../../@core/enums/wallet-type-id.enum";
import {IUserWallet} from "../services/interfaces/user-wallet.interface";

interface IAdditionalWallet {
  id: number;
  walletTypeId: number;
  title: string;
  balance: number;
}

@Component({
  selector: 'app-user-header',
  templateUrl: './user-header.component.html',
  styleUrls: ['./user-header.component.scss']
})
export class UserHeaderComponent implements OnDestroy {
  @Input() public user: IUser;
  @Input() public status: any;
  @Input() public isGoFast: boolean;
  public balanceTopPanel: ITopPanel;
  public checkInGame: { inGame: boolean; list: string[] };
  public isUserNameEditing: boolean = false;
  public isAdditionalWalletsExpanded: boolean = false;

  public additionalWallets$: Observable<IAdditionalWallet[]> = combineLatest([
    this.userService.getUserWallets(),
    this.walletTypesService.getAll(),
  ]).pipe(
    map(([wallets, types]) => {
      const titleById = new Map(types.map(t => [t.id, t.title]));
      return wallets
        .filter((w: IUserWallet) => w.walletType?.id !== WalletTypeIdEnum.REAL)
        .map((w: IUserWallet) => ({
          id: w.id,
          walletTypeId: w.walletType.id,
          title: titleById.get(w.walletType.id) || WalletTypeIdEnum[w.walletType.id] || `#${w.walletType.id}`,
          balance: w.balance,
        }));
    })
  );

  private readonly destroyed$: Subject<void> = new Subject();

  constructor(private userService: UserService,
              private walletTypesService: WalletTypesService,
              private statsService: StatsService,
              private router: Router,
              private dialog: MatDialog) {
    this.subscribeEmitters();
    this.userService.requestCheckInGame();

    if(!this.router.url.split('/').includes('main')) { // при загрузке /main не делаем запрос на стату, так как common stats компонент ее сделает сам
      const dateRange = {
        start: moment().subtract(1, 'days').toISOString(),
        end: moment().toISOString()
      }
      this.userService.requestCommonStat(dateRange);
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public openReferralInfo(referral: IReferral): void {
    this.dialog.open(ReferralInfoComponent, {
      data: referral
    })
  }

  public changeUserName(name: string): void {
    const userName = name.trim();
    if (!userName) {
      return;
    }
    this.userService.changeUserName(name).subscribe();
  }

  public setRandomAvatar(): void {
    this.userService.resetUserAvatar().subscribe(() => this.userService.requestUser());
  }

  public updateInGameStatus(): void {
    this.userService.requestCheckInGame();
  }

  public recalculateAccountType(): void {
    this.userService.recalculateAccountType().subscribe(() => this.userService.requestUser());
  }

  public recalculateAccountTypeWithDowngrade(): void {
    this.userService.recalculateAccountTypeWithDowngrade().subscribe(() => this.userService.requestUser());
  }

  public excludeFromStats(userId: number): void {
    this.statsService.excludeUserFromStat(userId, true).subscribe(() => this.userService.requestUser());
  }

  public showModeratorsLog(userId: number): void {
    this.router.navigate(['./moderation/activity-log'], {queryParams: {user: userId}});
  }


  public openChangeBalance(): void {
    const params = {
      width: '500px',
      height: 'fit-content',
      maxWidth: '40wv',
    }
    this.dialog.open(AddBalanceComponent, params)
      .afterClosed()
      .subscribe(() => this.userService.requestUser())
  }

  private subscribeEmitters(): void {
    this.userService.checkInGame$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => this.checkInGame = value);

    this.userService.commonStat$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((commonStats: ICommonStat) => {
        this.balanceTopPanel = commonStats.statistics.topPanel;
      })
  }
}
